import * as SelectPrimitive from "@radix-ui/react-select";
import { ComponentProps, useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { FilterMode } from "@/shared/lib/filter/filter";
import { Spinner } from "@/shared/components/ui/spinner";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { Button } from "@/shared/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/shared/components/ui/input-group";
import { GesecCustomer } from "../types/gesec-customer";
import {
  useBusinessCentralGesecCustomerListQuery,
  useBusinessCentralGesecCustomerShowQuery,
} from "@/services/backend/business-central/api/gesec-customer-api";

export function BusinessCentralGesecCustomerSelect({
  companyId,
  value,
  ...props
}: ComponentProps<typeof SelectPrimitive.Root> & { companyId: string }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const doSearch = () => {
    setSearch(searchInput);
    setSearchInput("");
  };

  const { data, isLoading, error, isFetching } =
    useBusinessCentralGesecCustomerListQuery(
      {
        companyId,
        name: {
          active: true,
          mode: FilterMode.Like,
          values: [search],
        },
      },
      { skip: !search || search.length < 3 },
    );

  const { data: selectedDebitor } = useBusinessCentralGesecCustomerShowQuery(
    { companyId, no: value ?? NullUUID },
    { skip: !value || value === "" },
  );

  const debitors: GesecCustomer[] = useMemo(() => {
    if (isLoading || isFetching) {
      return [];
    }
    const dataList = data?.value ?? [];
    if (!selectedDebitor) {
      return dataList;
    }
    return dataList.filter((d) => d.no !== selectedDebitor.no);
  }, [data?.value, isFetching, isLoading, selectedDebitor]);

  return (
    <Select value={value} {...props}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Debitor auswählen" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup className="mb-2 flex gap-2">
          <InputGroup
            className="border-0 shadow-none"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                doSearch();
              }
            }}
          >
            <InputGroupInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("Suche nach Debitor...")}
            />
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
          </InputGroup>
          <Button onClick={doSearch} variant="default">
            <Search />
          </Button>
        </SelectGroup>
        {selectedDebitor && (
          <SelectGroup>
            <SelectLabel>{t("Ausgewählt:")}</SelectLabel>
            <SelectItem value={selectedDebitor.no}>
              {selectedDebitor.name} {selectedDebitor.name2}
            </SelectItem>
          </SelectGroup>
        )}
        <SelectGroup>
          <SelectLabel>{t("Suchergebnisse:")}</SelectLabel>
          {(isLoading || isFetching) && (
            <SelectItem value="loading" disabled>
              <Spinner />
              <span>{t("Suche")}</span>
            </SelectItem>
          )}
          {error && (
            <SelectItem value="error" disabled>
              <RTKQueryErrorAlert error={error} />
            </SelectItem>
          )}
          {searchInput.length > 0 && data && debitors.length === 0 && (
            <SelectItem value={NullUUID} disabled>
              {t("Keine Debitoren gefunden")}
            </SelectItem>
          )}
          {debitors.map((debitor) => (
            <SelectItem value={debitor.no} key={debitor.no}>
              <span>
                {debitor.name} {debitor.name2}
              </span>
              <span className="text-muted-foreground text-sx">{`${debitor.address}, ${debitor.postCode}`}</span>
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
